import React, { useCallback } from 'react';
import { Modal } from 'antd';
import { useSelector } from 'react-redux';
import { TbFileAlert, TbInfoCircle } from 'react-icons/tb';
import { RootState } from '../../../store/store';
import { getAgreementProductLabel } from '../userAgreements';
import '../widgets/supplierCancellationPolicyNotice.less';

/**
 * Order-level notices for carts holding Israeli-law products (products whose
 * product_data carries `is_israel_law_enabled`, listed in
 * `ShoppingCartV2.userAgreements`). Adding such a product does NOT prompt:
 *
 *  - `confirmSaveOrder` — confirmation before an order / offer is saved
 *    (checkout, manage-order, the agent "Save offer"); `onConfirm` runs only
 *    when the user accepts, so nothing is saved on "Go Back".
 *  - `showOfferNotice` — the same notice, informative only, shown when the
 *    agent copies the saved offer link.
 *
 * Both list the affected products. Uses the static `Modal` API so it works from
 * any handler; it sits outside the app's ConfigProvider, so the RTL/LTR
 * direction is passed explicitly from the store.
 */
const useSupplierCancellationPolicyConfirm = () => {
  const { shoppingCartTranslations: t } = useSelector(
    (data: RootState) => data.config.translations
  );
  const direction = useSelector((data: RootState) => data.config.direction);
  const cart = useSelector(
    (state: RootState) => state.shoppingCartV2.ShoppingCartV2
  );

  const getAffectedProductLabels = useCallback((): string[] => {
    const identifiers = new Set(
      (cart?.userAgreements?.products_with_agreements ?? []).map(
        (entry) => entry.identifier
      )
    );
    return (cart?.products ?? [])
      .filter((product) => identifiers.has(String(product.identifier)))
      .map(getAgreementProductLabel)
      .filter(Boolean);
  }, [cart]);

  const renderContent = useCallback(
    ({
      variant,
      title,
      body,
    }: {
      variant: 'save-order' | 'offer-notice';
      title: string;
      body: string;
    }) => {
      const Icon = variant === 'offer-notice' ? TbInfoCircle : TbFileAlert;
      const labels = getAffectedProductLabels();
      return (
        <div className={`supplier-policy-confirm ${variant}`}>
          <div className="supplier-policy-confirm__icon" aria-hidden>
            <Icon size={30} />
          </div>
          <h3 className="supplier-policy-confirm__title">{title}</h3>
          <div className="supplier-policy-confirm__callout" role="note">
            <p className="supplier-policy-confirm__body">{body}</p>
            {labels.length > 0 && (
              <>
                <p className="supplier-policy-confirm__products-title">
                  {t.supplierPolicyAffectedProducts}
                </p>
                <ul className="supplier-policy-confirm__products">
                  {labels.map((label, index) => (
                    <li key={`${label}-${index}`}>{label}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      );
    },
    [getAffectedProductLabels, t]
  );

  const baseProps = {
    direction,
    // Header, icon and callout are laid out in `content` — antd's own
    // title/icon slots can't be centered or restyled.
    icon: null,
    className: 'supplier-policy-modal',
    okButtonProps: { className: 'btn-style-5' },
    maskClosable: false,
    centered: true,
    width: 540,
    // Sit above the cart drawer / bot panel (z-index up to 9999).
    zIndex: 10000,
  };

  const confirmSaveOrder = useCallback(
    (onConfirm: () => void) => {
      Modal.confirm({
        ...baseProps,
        content: renderContent({
          variant: 'save-order',
          title: t.supplierPolicySaveOrderTitle,
          body: t.supplierPolicySaveOrderBody,
        }),
        okText: t.supplierPolicySaveOrderOk,
        cancelText: t.supplierPolicySaveOrderCancel,
        cancelButtonProps: { className: 'supplier-policy-modal__cancel' },
        // Never accepted by a stray Enter or backdrop click.
        autoFocusButton: 'cancel',
        onOk: onConfirm,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [direction, renderContent, t]
  );

  const showOfferNotice = useCallback(() => {
    Modal.info({
      ...baseProps,
      content: renderContent({
        variant: 'offer-notice',
        title: t.supplierPolicyOfferNoticeTitle,
        body: t.supplierPolicyOfferNoticeBody,
      }),
      okText: t.supplierPolicyOfferNoticeOk,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction, renderContent, t]);

  return { confirmSaveOrder, showOfferNotice };
};

export default useSupplierCancellationPolicyConfirm;
